import type {
    Author,
    Category,
    IndustrySector,
    InvestorsIntelligenceSector,
    Product,
    SearchableTicker,
    Tag
} from "@/types/entities";

import { ArticlesSyncCoordinator } from "@/services/data/sync/articles-sync-coordinator";
import { BonusReportsSyncCoordinator } from "@/services/data/sync/bonus-reports-sync-coordinator";
import { SectorHistorySyncCoordinator } from "@/services/data/sync/sector-history-sync-coordinator";
import { TradeAlertsSyncCoordinator } from "@/services/data/sync/trade-alerts-sync-coordinator";
import { WebinarsSyncCoordinator } from "@/services/data/sync/webinars-sync-coordinator";

import userPermissionsFactory, { UserPermissions } from "@/types/permissions";

import { loadAuthorMap } from "@/services/data/sync/author-sync-coordinator";
import { loadCategoryMap } from "@/services/data/sync/category-sync-coordinator";
import { loadIndustrySectorMap } from "@/services/data/sync/industry-sector-sync-coordinator";
import { loadProductMap } from "@/services/data/sync/product-sync-coordinator";
import { loadSectorMaps } from "@/services/data/sync/sector-sync-coordinator";
import { loadTagMap } from "@/services/data/sync/tag-sync-coordinator";
import { loadTickerMap } from "@/services/data/sync/ticker-sync-coordinator";
import {
    articleKey,
    authorsSyncKey,
    bonusReportKey,
    categoriesSyncKey,
    industrySectorsSyncKey,
    productsSyncKey,
    sectorAlertsKey,
    sectorsSyncKey,
    simplePagesSyncKey,
    syncThrottlingKey,
    tagsSyncKey,
    tickersSyncKey,
    tradeAlertKey,
    webinarKey
} from "@/types/products/product-definitions";
import { refreshSimplePageCache } from "@/services/data/sync/simple-page-sync-coordinator";
import { notifyUpdate } from "@/services/data/sync-worker";
import LocalForagePlugin, { syncState } from "@/plugins/LocalForagePlugin";
import { DateTime } from "luxon";

export type CompositionContext = {
    getAuthorById: ( id: string ) => Author | undefined,
    getCategoryById: ( id: string ) => Category | undefined,
    getIndustrySectorById: ( id: string ) => IndustrySector | undefined,
    getProductById: ( id: string ) => Product | undefined,
    getSectorById: ( id: string ) => InvestorsIntelligenceSector | undefined,
    getSectorByTmiId: ( id: number ) => InvestorsIntelligenceSector | undefined,
    getTagById: ( id: string ) => Tag | undefined,
    getTickerById: ( id: string ) => SearchableTicker | undefined
}

export interface IDataSyncSupervisor {
    patchRepositories: ( authToken: string | null, authorizationGrants: string | null ) => Promise< void >;
}

class DataSyncSupervisor implements IDataSyncSupervisor {
    readonly #authorsMap = new Map< string, Author >();
    readonly #categoriesMap = new Map< string, Category >();
    readonly #industrySectorsMap = new Map< string, IndustrySector >();
    readonly #productsMap = new Map< string, Product >();
    readonly #sectorsIdMap = new Map< string, InvestorsIntelligenceSector >();
    readonly #sectorsTmiIdMap = new Map< number, InvestorsIntelligenceSector >();
    readonly #tagsMap = new Map< string, Tag >();
    readonly #tickersMap = new Map< string, SearchableTicker >();

    public async patchRepositories( authToken: string | null, authorizationGrants: string | null ): Promise< void > {
        if ( typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( syncThrottlingKey );
        if ( null != syncThresholdInMillis && syncThresholdInMillis > DateTime.utc().toMillis() ) {
            return;
        }

        // Set the sync cooldown period
        const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 30 } ).toMillis();
        await syncState.setItem( syncThrottlingKey, nextSyncThresholdInMillis );

        // Update and load all auxiliary content models
        await this.loadDependencies( authToken );

        const compositionCtx: CompositionContext = {
            getAuthorById: ( id: string ) => this.#authorsMap.get( id ),
            getCategoryById: ( id: string ) => this.#categoriesMap.get( id ),
            getIndustrySectorById: ( id: string ) => this.#industrySectorsMap.get( id ),
            getProductById: ( id: string ) => this.#productsMap.get( id ),
            getSectorById: ( id: string ) => this.#sectorsIdMap.get( id ),
            getSectorByTmiId: ( id: number ) => this.#sectorsTmiIdMap.get( id ),
            getTagById: ( id: string ) => this.#tagsMap.get( id ),
            getTickerById: ( id: string ) => this.#tickersMap.get( id )
        };

        // TODO: log when the authToken exists, but the authorization grants does not
        if ( !!authToken && !!authorizationGrants ) {
            const permissions: UserPermissions = userPermissionsFactory.create( JSON.parse( authorizationGrants ) );

            if ( permissions.hasArticleAccess() ) {
                // Build the articles coordinator
                const articlesSyncCoordinator = new ArticlesSyncCoordinator( compositionCtx );

                // Update the IDB collection for all articles
                if ( await articlesSyncCoordinator.patchRepository( authToken ) > 0 ) {
                    notifyUpdate( articleKey );
                }
            }

            if ( permissions.hasBonusReportAccess() ) {
                // Build the bonus reports coordinator
                const bonusReportsSyncCoordinator = new BonusReportsSyncCoordinator( compositionCtx );

                // Update the IDB collection for all bonus reports
                if ( await bonusReportsSyncCoordinator.patchRepository( authToken ) > 0 ) {
                    notifyUpdate( bonusReportKey );
                }
            }

            if ( permissions.hasProductTradeAlertsAccess() ) {
                // Build the trade alerts coordinator
                const tradeAlertsSyncCoordinator = new TradeAlertsSyncCoordinator( compositionCtx );

                // Update the IDB collection for all trade alerts
                if ( await tradeAlertsSyncCoordinator.patchRepository( authToken ) > 0 ) {
                    notifyUpdate( tradeAlertKey );
                }
            }

            if ( permissions.hasWebinarAccess() ) {
                // Build the trade alerts coordinator
                const webinarsSyncCoordinator = new WebinarsSyncCoordinator( compositionCtx );

                // Update the IDB collection for all webinars
                if ( await webinarsSyncCoordinator.patchRepository( authToken ) > 0 ) {
                    notifyUpdate( webinarKey );
                }
            }

            if ( permissions.hasSectorAccess() && permissions.hasSectorAlertsAccess() ) {
                // Build the trade alerts coordinator
                const sectorHistorySyncCoordinator = new SectorHistorySyncCoordinator( compositionCtx );

                // Update the IDB collection for sector history
                if ( await sectorHistorySyncCoordinator.patchRepository( authToken ) ) {
                    notifyUpdate( sectorAlertsKey );
                }
            }
        }
    }

    private async loadDependencies( authToken: string | null ): Promise< void > {
        // Pull down all meta-data for all content items

        // Perform the sync with tickers first since if they don't have anything cached, the lack of data could
        // cause "weirdness" in the search capabilities
        if ( await loadTickerMap( this.#tickersMap ) ) {
            notifyUpdate( tickersSyncKey );
        }

        if ( await loadAuthorMap( this.#authorsMap ) ) {
            notifyUpdate( authorsSyncKey );
        }

        if ( await loadCategoryMap( this.#categoriesMap ) ) {
            notifyUpdate( categoriesSyncKey );
        }

        if ( await loadIndustrySectorMap( this.#industrySectorsMap ) ) {
            notifyUpdate( industrySectorsSyncKey );
        }

        if ( await loadProductMap( this.#productsMap ) ) {
            notifyUpdate( productsSyncKey );
        }

        if ( await loadSectorMaps( this.#sectorsIdMap, this.#sectorsTmiIdMap, authToken ) ) {
            notifyUpdate( sectorsSyncKey );
        }

        if ( await loadTagMap( this.#tagsMap ) ) {
            notifyUpdate( tagsSyncKey );
        }

        if ( await refreshSimplePageCache() ) {
            notifyUpdate( simplePagesSyncKey );
        }
    }
}

const syncSupervisor: IDataSyncSupervisor = new DataSyncSupervisor();
export { syncSupervisor };
